import { Panel as ResizablePanel } from 'react-resizable-panels'
import { EventTypes, logEvent } from '@unified-lk/event-log'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../types/plans'
import { DraggableMisService } from './MisDragSource'
import type { MisServiceItem, MisDragItem } from './constructorTypes'
import {
  isAiGeneratedMisPlan,
  misImportedPlanDateLabel,
  misImportedPlanDoctorLabel,
  misImportedPlanComposeTimeLabel,
  misImportedPlanTitleForSidebar,
  type MisImportedPlanSummary,
} from './auto-distribute/selectedPlanServices'

function misPlanAmountRub(plan: MisImportedPlanSummary): number {
  const minor = plan.total_estimated_minor
  if (typeof minor === 'number' && minor > 0) return minor / 100
  return 0
}

export function MisPlansSidebar({
  bootstrap,
  session,
  misPlans,
  misLoading,
  misSearch,
  expandedMisPlanIds,
  misServicesCache,
  misServicesLoadingIds,
  selectedMisIds,
  allSelectedItems,
  usedMisServiceKeys,
  patientName,
  patientId,
  onMisSearchChange,
  onToggleMisPlan,
  onToggleMisSelected,
  onClearMisSelection,
  onRefresh,
  onLoadMisPlanServices,
  onCorrectPlan,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  misPlans: MisImportedPlanSummary[]
  misLoading: boolean
  misSearch: string
  expandedMisPlanIds: string[]
  misServicesCache: Record<string, MisServiceItem[]>
  misServicesLoadingIds: string[]
  selectedMisIds: Set<string>
  allSelectedItems: MisDragItem[]
  usedMisServiceKeys: Set<string>
  patientName: string
  patientId: number | null | undefined
  onMisSearchChange: (value: string) => void
  onToggleMisPlan: (planId: string, title?: string) => void
  onToggleMisSelected: (id: string) => void
  onClearMisSelection: () => void
  onRefresh: () => void
  onLoadMisPlanServices: (planId: string) => void
  onCorrectPlan?: (plan: MisImportedPlanSummary) => void
}) {
  return (
    <ResizablePanel
      id="left-rail-mis"
      defaultSize="62%"
      minSize="20%"
      className="flex min-h-0 flex-col"
    >
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-2.5 py-1.5">
        <h3 className="flex-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-600">
          Планы МИС пациента <span className="text-slate-400">({misPlans.length})</span>
        </h3>
        {selectedMisIds.size > 0 ? (
          <button
            type="button"
            className="rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 hover:bg-blue-100"
            onClick={onClearMisSelection}
            title="Снять выделение"
          >
            Снять ({selectedMisIds.size})
          </button>
        ) : null}
        <button
          type="button"
          className="rounded border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          onClick={onRefresh}
          disabled={!patientId}
          title="Обновить из МИС"
        >
          Обновить
        </button>
      </div>
      <div className="bg-blue-50 px-2.5 py-1 text-[10px] text-blue-700">
        Источник: МИС пациента «{patientName?.trim() || `ID ${patientId ?? '—'}`}»
      </div>
      <div className="border-b border-slate-200 px-2 py-1.5">
        <input
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs placeholder-slate-400 focus:border-blue-400 focus:outline-none"
          placeholder="Поиск по плану / врачу..."
          value={misSearch}
          onChange={(event) => onMisSearchChange(event.target.value)}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!patientId ? (
          <p className="py-3 text-center text-[11px] text-slate-400">Пациент не указан</p>
        ) : misLoading ? (
          <p className="py-3 text-center text-[11px] text-slate-400">Загрузка планов МИС...</p>
        ) : misPlans.length === 0 ? (
          <p className="px-3 py-3 text-center text-[11px] text-slate-400">
            У пациента нет планов в МИС.<br />После «Применить план» из Diagnocat нажмите «Обновить».
          </p>
        ) : (
          <div>
            {misPlans
              .filter((plan) => {
                if (!misSearch.trim()) return true
                const q = misSearch.trim().toLowerCase()
                const title = (plan.plan_title ?? '').toLowerCase()
                const doctor = misImportedPlanDoctorLabel(plan).toLowerCase()
                return title.includes(q) || doctor.includes(q)
              })
              .map((plan) => {
                const planId = plan.plan_id
                const services = misServicesCache[planId] ?? []
                const isServicesLoading = misServicesLoadingIds.includes(planId)
                const expanded = expandedMisPlanIds.includes(planId)
                const totalPositions = plan.item_count ?? services.length
                const computedAmount = services.length > 0
                  ? services.reduce((sum, s) => {
                      const qty = s.quantity ?? 1
                      return sum + (s.total_price ?? (s.unit_price ?? 0) * qty)
                    }, 0)
                  : null
                const planAmount = misPlanAmountRub(plan) > 0
                  ? misPlanAmountRub(plan)
                  : (computedAmount ?? 0)
                const formattedDate = misImportedPlanDateLabel(plan)
                const composeTime = misImportedPlanComposeTimeLabel(plan)
                const title = (plan.plan_title ?? '').trim() || 'План лечения'
                const isAi = isAiGeneratedMisPlan(plan)

                return (
                  <div key={planId} className="border-b border-slate-200 last:border-b-0">
                    <button
                      type="button"
                      className={`flex w-full items-center gap-1.5 px-2 py-1 text-left text-[11px] hover:bg-slate-50 ${
                        expanded ? 'bg-slate-100' : ''
                      }`}
                      onClick={() => {
                        onToggleMisPlan(planId, title)
                        if (!expanded) {
                          onLoadMisPlanServices(planId)
                          logEvent(bootstrap.apiBase, session.accessToken, EventTypes.MIS_PLAN_EXPANDED, {
                            mis_plan_id: planId,
                            mis_plan_title: title,
                          })
                        }
                      }}
                    >
                      <svg className={`h-3 w-3 flex-shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.25" d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="w-[4.25rem] shrink-0 text-[10px] leading-tight text-slate-500">
                        <span className="block truncate">{formattedDate}</span>
                        {composeTime ? (
                          <span className="block truncate text-[9px] font-medium text-violet-700" title="Время составления ИИ-плана">
                            {composeTime}
                          </span>
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1 truncate font-medium text-slate-700">
                          <span className="truncate">{title}</span>
                          {isAi ? (
                            <span className="shrink-0 rounded bg-violet-100 px-1 py-px text-[9px] font-bold uppercase text-violet-800" title="Составлен с помощью ИИ">
                              ИИ
                            </span>
                          ) : null}
                        </span>
                        <span className="block truncate text-[10px] text-slate-500">
                          {misImportedPlanDoctorLabel(plan)}
                          {composeTime ? (
                            <span className="text-violet-600"> · составлен {composeTime}</span>
                          ) : null}
                        </span>
                      </span>
                      <span className="w-9 shrink-0 truncate text-right text-[10px] text-slate-500">
                        {totalPositions} поз.
                      </span>
                      <span className="w-16 shrink-0 truncate text-right text-[10px] font-medium text-slate-600">
                        {Number(planAmount).toLocaleString('ru-RU')} ₽
                      </span>
                    </button>
                    {isAi && onCorrectPlan ? (
                      <div className="flex justify-end border-b border-slate-100 bg-slate-50 px-2 py-0.5">
                        <button
                          type="button"
                          className="rounded border border-violet-300 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-800 hover:bg-violet-100"
                          title="Уточнить и пересобрать план с помощью ИИ"
                          onClick={() => onCorrectPlan(plan)}
                        >
                          Корректировка
                        </button>
                      </div>
                    ) : null}
                    {expanded ? (
                      <div className="bg-slate-50 px-1 py-1">
                        {isServicesLoading ? (
                          <p className="px-2 py-1 text-[10px] text-slate-400">Загружаем позиции из MIS...</p>
                        ) : services.length === 0 ? (
                          <p className="px-2 py-1 text-[10px] text-slate-400">В плане нет позиций</p>
                        ) : (
                          <div className="rounded border border-slate-200 bg-white">
                            {services.map((service) => {
                              const misKey = `${planId}:${service.service_name ?? ''}:${service.tooth_number ?? ''}`
                              return (
                                <DraggableMisService
                                  key={service.id}
                                  service={service}
                                  misPlanUuid={planId}
                                  selected={selectedMisIds.has(service.id)}
                                  selectionCount={selectedMisIds.size}
                                  onToggleSelect={() => onToggleMisSelected(service.id)}
                                  selectedItems={allSelectedItems}
                                  isUsed={usedMisServiceKeys.has(misKey)}
                                />
                              )
                            })}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )
              })}
          </div>
        )}
      </div>
    </ResizablePanel>
  )
}
